import { hostFromSelection, type AppConfig, type EditorState } from '@xsscool/shared';

export class ApiError extends Error {
  status: number;
  hint?: string;
  constructor(status: number, message: string, hint?: string) {
    super(message);
    this.status = status;
    this.hint = hint;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init });
  if (!res.ok) {
    let message = `${init.method ?? 'GET'} ${path} failed: ${res.status}`;
    let hint: string | undefined;
    try {
      const body = (await res.json()) as { error?: string; hint?: string };
      if (body.error) message = body.error;
      hint = body.hint;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message, hint);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function jsonInit(method: string, body: unknown): RequestInit {
  return { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
}

export interface ShareSummary {
  id: string;
  name: string | null;
  preview: string;
  status: number | null;
  createdAt: number;
  updatedAt: number;
  expiresAt: number | null;
  submittedPayloadId: string | null;
  owned: boolean;
  replayUrl: string;
  editorUrl: string;
  curlExample: string;
  jsExample: string;
}

export interface ShareDetail extends ShareSummary {
  state: EditorState | null;
}

export interface SharePatch {
  state?: EditorState;
  name?: string | null;
  ttlDays?: number | null;
  submittedPayloadId?: string | null;
}

export function saveShare(state: EditorState, name?: string): Promise<ShareDetail> {
  return request<ShareDetail>('/api/shares', jsonInit('POST', { state, name: name?.trim() || undefined }));
}

export function updateShare(id: string, patch: SharePatch): Promise<ShareDetail> {
  return request<ShareDetail>(`/api/shares/${id}`, jsonInit('PATCH', patch));
}

export async function listMyShares(): Promise<ShareSummary[]> {
  try {
    const data = await request<{ shares: ShareSummary[] }>('/api/shares/mine');
    return data.shares;
  } catch {
    return [];
  }
}

export async function getShare(id: string): Promise<ShareDetail | null> {
  try {
    return await request<ShareDetail>(`/api/shares/${id}`);
  } catch {
    return null;
  }
}

export async function deleteShare(id: string): Promise<void> {
  try {
    await request<void>(`/api/shares/${id}`, { method: 'DELETE' });
  } catch (e) {
    if (!(e instanceof ApiError && e.status === 404)) throw e;
  }
}

export async function fetchApiToken(): Promise<string> {
  const data = await request<{ token: string }>('/api/token');
  return data.token;
}

export function fetchAppConfig(): Promise<AppConfig> {
  return request<AppConfig>('/api/config');
}

export interface DomainOpts {
  domainBase: string;
  domainSub?: string;
}

// Same-origin URL for internal API calls and SSE streams. Always points at
// the host the editor was loaded from (so cookies, CORS, and EventSource
// keep working regardless of the user's display-domain selection).
export function absoluteUrl(rel: string): string {
  if (typeof window === 'undefined') return rel;
  const { protocol, hostname, port } = window.location;
  const target = port === '5173' ? `${protocol}//${hostname}:8080` : window.location.origin;
  return `${target}${rel.startsWith('/') ? rel : `/${rel}`}`;
}

// True when the editor is loaded at a real public host. False for dev/test
// (localhost, raw IPs, .local/.test). Display-domain rewriting only kicks in
// in prod-like setups so localhost dev keeps generating reachable URLs.
function isProductionHost(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  if (!h || h === 'localhost') return false;
  if (/^\d+\.\d+\.\d+\.\d+$/.test(h)) return false;
  if (/^[0-9a-f:]+$/i.test(h)) return false;
  if (h.endsWith('.local') || h.endsWith('.test')) return false;
  return true;
}

// User-facing URL for share/copy/display. Uses the selected display domain
// so the editor can hand out e.g. https://ctf.baby/s/<id> while still
// hitting its own origin for the underlying API call. In dev/test, falls
// back to the current origin so generated URLs stay reachable.
export function displayUrl(rel: string, opts: DomainOpts): string {
  if (!isProductionHost()) return absoluteUrl(rel);
  const host = hostFromSelection({ base: opts.domainBase, sub: opts.domainSub });
  return `https://${host}${rel.startsWith('/') ? rel : `/${rel}`}`;
}
